import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import {
  Bell,
  ChevronDown,
  Globe,
  LogOut,
  Settings,
  User,
} from 'lucide-react';

import { useAuth } from '../../providers/AuthProvider';
import { setLanguage, type SupportedLanguage, languageConfig } from '../../i18n/config';

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick: _onMenuClick }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();

  const currentLanguage = i18n.language as SupportedLanguage;
  const otherLanguage: SupportedLanguage = currentLanguage === 'ar' ? 'en' : 'ar';

  const handleLanguageSwitch = () => {
    setLanguage(otherLanguage);
  };

  return (
    <header className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-x-4 border-b border-secondary-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
      {/* Left section */}
      <div className="flex flex-1 gap-x-4 lg:gap-x-6">
        {/* Breadcrumb or page title could go here */}
      </div>

      {/* Right section */}
      <div className="flex items-center gap-x-4 lg:gap-x-6">
        {/* Language switcher */}
        <button
          type="button"
          onClick={handleLanguageSwitch}
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
          aria-label={t('switchLanguage')}
        >
          <Globe className="h-5 w-5" />
          <span className="hidden sm:inline">
            {languageConfig[otherLanguage].nativeName}
          </span>
        </button>

        {/* Notifications */}
        <button
          type="button"
          className="relative rounded-full p-2 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-600"
          aria-label={t('notifications', { defaultValue: 'Notifications' })}
        >
          <Bell className="h-5 w-5" />
          {/* Notification badge */}
          <span className="absolute top-1.5 ltr:right-1.5 rtl:left-1.5 h-2 w-2 rounded-full bg-red-500" />
        </button>

        {/* Separator */}
        <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-secondary-200" />

        {/* User dropdown */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center gap-x-3 rounded-md p-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <User className="h-4 w-4" />
            </div>
            <span className="hidden lg:block">
              {user?.displayName || user?.username}
            </span>
            <ChevronDown className="hidden h-4 w-4 text-secondary-400 lg:block" />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute ltr:right-0 rtl:left-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {/* User info */}
              <div className="px-4 py-3 border-b border-secondary-100">
                <p className="text-sm font-medium text-secondary-900">
                  {user?.displayName || user?.username}
                </p>
                <p className="text-sm text-secondary-500 truncate">
                  {user?.email}
                </p>
              </div>

              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={`
                      w-full flex items-center px-4 py-2 text-sm
                      ${active ? 'bg-secondary-50 text-secondary-900' : 'text-secondary-700'}
                    `}
                  >
                    <User className="h-4 w-4 ltr:mr-3 rtl:ml-3" />
                    {t('profile', { defaultValue: 'Profile' })}
                  </button>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={`
                      w-full flex items-center px-4 py-2 text-sm
                      ${active ? 'bg-secondary-50 text-secondary-900' : 'text-secondary-700'}
                    `}
                  >
                    <Settings className="h-4 w-4 ltr:mr-3 rtl:ml-3" />
                    {t('navigation.settings')}
                  </button>
                )}
              </Menu.Item>

              <div className="border-t border-secondary-100 my-1" />

              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={logout}
                    className={`
                      w-full flex items-center px-4 py-2 text-sm text-red-600
                      ${active ? 'bg-red-50' : ''}
                    `}
                  >
                    <LogOut className="h-4 w-4 ltr:mr-3 rtl:ml-3" />
                    {t('auth:logout.logoutButton')}
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}
